import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import { ReactRenderer } from "@tiptap/react";
import tippy from "tippy.js";
import { EditorToolbar } from "./EditorToolbar";
import { MentionList } from "./MentionList";
import { usersData, groupsData } from "./MentionEditor";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Image from "@tiptap/extension-image";
import {
  Smile,
  Send,
  Paperclip,
  X,
  FileText,
  File,
  FileArchive,
  Film,
  ImageIcon,
  Loader2,
} from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import { useRecentEmojis } from "../hooks/useRecentEmojis";
import { FileViewerModal } from "./FileViewerModal";

const getFileTypeIcon = (type) => {
  if (type.startsWith("image/")) return <ImageIcon size={18} />;
  if (type.includes("pdf") || type.includes("doc") || type.includes("txt"))
    return <FileText size={18} />;
  if (type.includes("zip") || type.includes("rar") || type.includes("7z"))
    return <FileArchive size={18} />;
  if (type.startsWith("video/")) return <Film size={18} />;
  return <File size={18} />;
};

function formatFileSize(bytes) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

export function MentionEditor({
  onPublish,
  onChange,
  placeholder = "Write your thoughts...",
  initialContent = "",
  buttonText = "Publicar",
  existingFiles = [],
  onRemoveExistingFile,
  backendUrl,
  onlyTextInput,
  isComment,
  isUploading,
}) {
  const { recentEmojis, addRecentEmoji } = useRecentEmojis();

  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [filePreviews, setFilePreviews] = React.useState([]);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [selectedUser, setSelectedUser] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState([]);

  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const initializedRef = useRef(false);

  const mentionData = [...usersData, ...groupsData];

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Image.configure({
        HTMLAttributes: {
          class: "rounded-lg max-w-full h-auto",
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
        alignments: ["left", "center", "right"],
        defaultAlignment: "left",
      }),
      Mention.configure({
        HTMLAttributes: {
          class: "mention",
        },
        renderLabel({ options, node }) {
          return `@${node.attrs.label ?? node.attrs.id}`;
        },
        suggestion: {
          items: ({ editor, query }) => {
            return mentionData.filter((user) => {
              return user.value.toLowerCase().includes(query.toLowerCase());
            });
          },
          render: () => {
            let reactRenderer;
            let popup;

            return {
              onStart: (props) => {
                const enhancedProps = {
                  ...props,
                  onSelectUser: (user) => {
                    setSelectedUser((prevUsers = []) => {
                      // Ensure prevUsers is an array
                      const userArray = Array.isArray(prevUsers)
                        ? prevUsers
                        : [];
                      if (!userArray.some((u) => u.id === user.id)) {
                        return [...userArray, user];
                      }
                      return userArray;
                    });
                  },
                  onSelectGroup: (group) => {
                    setSelectedGroup((prevGroup = []) => {
                      // Ensure prevGroup is an array
                      const groupArray = Array.isArray(prevGroup)
                        ? prevGroup
                        : [];
                      if (!groupArray.some((g) => g.id === group.id)) {
                        return [...groupArray, group];
                      }
                      return groupArray;
                    });
                  },
                };

                reactRenderer = new ReactRenderer(MentionList, {
                  props: enhancedProps,
                  editor: props.editor,
                });

                popup = tippy("body", {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: reactRenderer.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: "manual",
                  placement: "bottom-start",
                });
              },

              onUpdate(props) {
                reactRenderer.updateProps(props);
                popup[0].setProps({
                  getReferenceClientRect: props.clientRect,
                });
              },
              onKeyDown(props) {
                if (props.event.key === "Escape") {
                  popup[0].hide();
                  return true;
                }
                return false;
              },
              onExit() {
                popup[0].destroy();
                reactRenderer.destroy();
              },
            };
          },
        },
      }),
    ],
    editorProps: {
      attributes: {
        class:
          "editor-content focus:outline-none min-h-[100px] prose prose-lg max-w-none",
        placeholder,
      },
    },
    content: initialContent,
    autofocus: true,
    editable: !isUploading,
  });

  // Update editor editable status when isUploading changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!isUploading);
    }
  }, [editor, isUploading]);

  useEffect(() => {
    if (!initializedRef.current && existingFiles && existingFiles.length > 0) {
      const existingFilePreviews = existingFiles.map((file) => {
        const fileName = file.name || file.url.split("/").pop() || "file";
        const fileType = file.mime.toLowerCase().includes("pdf")
          ? "application/pdf"
          : file.mime.toLowerCase().match(/doc|docx/)
          ? "application/msword"
          : file.mime.toLowerCase().match(/jpg|jpeg|png|gif|webp/)
          ? `image/${file.mime.split("/").pop()}`
          : "application/octet-stream";

        return {
          id: file.id,
          name: fileName,
          type: fileType,
          size: 0,
          url: file.url,
          isExisting: true,
        };
      });

      setFilePreviews(existingFilePreviews);
      initializedRef.current = true;
    }
  }, [existingFiles]);

  useEffect(() => {
    if (editor && initialContent) {
      if (initialContent.startsWith("@")) {
        const userName = initialContent.slice(1).trim();
        const user = mentionData.find(
          (user) => user.value.toLowerCase() === userName.toLowerCase()
        );

        if (user) {
          setSelectedUser(user);
          setSelectedGroup(user);
          editor.commands.setContent("");

          editor.commands.insertContent({
            type: "mention",
            attrs: {
              id: user.id,
              label: user.value.toLowerCase().replace(/\s+/g, ""),
            },
          });

          editor.commands.insertContent(" ");

          setTimeout(() => {
            editor.commands.focus("end");
          }, 0);
        }
      } else {
        editor.commands.setContent(initialContent);
        setTimeout(() => {
          editor.commands.focus("end");
        }, 0);
      }
    }
    if (editor && onChange) {
      const content = editor.getHTML();
      const files = filePreviews
        .filter((preview) => preview.file)
        .map((preview) => preview.file);
      onChange(content, files);
    }
  }, [editor, initialContent]);

  useEffect(() => {
    if (editor && onChange) {
      const handleUpdate = () => {
        const content = editor.getHTML();
        const mentionedIds =
          Array.from(content.matchAll(/data-id="(\d+)"/g)).map(
            (match) => match[1]
          ) || [];

        if (mentionedIds.length > 0) {
          setSelectedUser((prevUsers = []) => {
            const userArray = Array.isArray(prevUsers) ? prevUsers : [];
            return userArray.filter((user) =>
              (mentionedIds || []).includes(user.id.toString())
            );
          });
        }
      };

      editor.on("update", handleUpdate);
      return () => {
        editor.off("update", handleUpdate);
      };
    }
  }, [editor, onChange]);

  useEffect(() => {
    if (editor && onChange) {
      const handleUpdate = () => {
        const content = editor.getHTML();

        const currentFiles = filePreviews
          .filter((preview) => preview.file)
          .map((preview) => preview.file);

        onChange(content, currentFiles);
      };

      editor.on("update", handleUpdate);

      const initialContent = editor.getHTML();
      const initialFiles = filePreviews
        .filter((preview) => preview.file)
        .map((preview) => preview.file);

      onChange(initialContent, initialFiles);

      return () => {
        editor.off("update", handleUpdate);
      };
    }
  }, [editor, filePreviews]);

  const handlePublish = () => {
    if (editor && (editor.getText().trim() || filePreviews.length > 0)) {
      const content = editor.getHTML();
      const files = filePreviews
        .filter((preview) => preview.file)
        .map((preview) => preview.file);

      const mentionedIds =
        Array.from(content.matchAll(/data-id="(\d+)"/g)).map(
          (match) => match[1]
        ) || [];

      let userArray = [];
      if (Array.isArray(selectedUser)) {
        userArray = selectedUser.filter(
          (user) =>
            user &&
            typeof user === "object" &&
            mentionedIds.includes(user.id.toString())
        );
      }

      let groupArray = [];
      if (Array.isArray(selectedGroup)) {
        groupArray = selectedGroup.filter(
          (group) =>
            group &&
            typeof group === "object" &&
            mentionedIds.includes(group.id.toString())
        );
      }

      if (initialContent && initialContent.startsWith("@")) {
        const userName = initialContent.slice(1).trim();
        const originalMentioned = mentionData.find(
          (user) => user.value.toLowerCase() === userName.toLowerCase()
        );

        if (
          originalMentioned &&
          !userArray.some((u) => u.id === originalMentioned.id)
        ) {
          userArray.push(originalMentioned);
        }
      }

      onPublish(content, files, userArray, groupArray);

      filePreviews.forEach((preview) => {
        if (!preview.isExisting && preview.url) {
          URL.revokeObjectURL(preview.url);
        }
      });

      editor.commands.clearContent();
      setFilePreviews([]);
      initializedRef.current = false;
      setHasSubmitted(true);
    }
  };

  const handleEmojiSelect = (emojiData) => {
    if (editor && !isUploading) {
      editor.commands.insertContent(emojiData.emoji);
      addRecentEmoji(emojiData.emoji);
    }
    setShowEmojiPicker(false);
  };

  const insertRecentEmoji = (emoji) => {
    if (editor && !isUploading) {
      editor.commands.insertContent(emoji);
      addRecentEmoji(emoji);
    }
  };

  const handleFileSelect = async (files) => {
    if (!files || isUploading) return;

    const maxFileSize = 600 * 1024 * 1024; // 600MB
    const newPreviews = [];

    for (const file of Array.from(files)) {
      if (file.size > maxFileSize) {
        console.error(`File ${file.name} is too large. Maximum size is 600MB.`);
        continue;
      }

      try {
        const url = URL.createObjectURL(file);
        const preview = {
          id: Math.random().toString(36).substring(7),
          name: file.name,
          type: file.type || "application/octet-stream",
          size: file.size,
          file,
          url,
          isExisting: false,
        };
        newPreviews.push(preview);
      } catch (error) {
        console.error("Error creating preview for file:", file.name, error);
      }
    }

    setFilePreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeFile = async (id) => {
    if (isUploading) return;

    setFilePreviews((prev) => {
      const preview = prev.find((p) => p.id === id);
      if (preview?.isExisting && onRemoveExistingFile) {
        onRemoveExistingFile(preview);
      } else if (preview?.file) {
        URL.revokeObjectURL(preview.url);
      }
      return prev.filter((p) => p.id !== id);
    });
  };

  const openFile = (preview) => {
    if (hasSubmitted) {
      setSelectedFile(preview);
    } else {
      window.open(preview.url, "_blank");
    }
  };

  useEffect(() => {
    const dropZone = dropZoneRef.current;
    if (!dropZone) return;

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isUploading) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (isUploading) return;

      const files = e.dataTransfer?.files;
      handleFileSelect(files);
    };

    const handlePaste = (e) => {
      if (isUploading) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles = Array.from(items)
        .filter((item) => item.type.startsWith("image/"))
        .map((item) => item.getAsFile())
        .filter((file) => file !== null);

      if (imageFiles.length > 0) {
        handleFileSelect(imageFiles);
      }
    };

    dropZone.addEventListener("dragover", handleDragOver);
    dropZone.addEventListener("dragleave", handleDragLeave);
    dropZone.addEventListener("drop", handleDrop);
    document.addEventListener("paste", handlePaste);

    return () => {
      dropZone.removeEventListener("dragover", handleDragOver);
      dropZone.removeEventListener("dragleave", handleDragLeave);
      dropZone.removeEventListener("drop", handleDrop);
      document.removeEventListener("paste", handlePaste);
    };
  }, [isUploading]);

  useEffect(() => {
    return () => {
      filePreviews.forEach((preview) => {
        if (preview.file && !preview.isExisting) {
          URL.revokeObjectURL(preview.url);
        }
      });
    };
  }, [filePreviews]);

  if (!editor) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg flex flex-col border border-gray-200">
      <EditorToolbar editor={editor} disabled={isUploading} />
      <div
        ref={dropZoneRef}
        className={`px-3 py-2 flex-1 relative ${
          isDragging ? "bg-blue-50 ring-2 ring-blue-500 ring-opacity-50" : ""
        } ${isUploading ? "opacity-75 cursor-not-allowed" : ""}`}
      >
        <EditorContent editor={editor} className="min-h-[100px] " />
        {isDragging && !isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-blue-500 bg-opacity-10 pointer-events-none">
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm text-blue-600 font-medium flex items-center gap-2">
              <Paperclip size={18} />
              Drop files here
            </div>
          </div>
        )}

        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 pointer-events-none">
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm text-gray-600 font-medium flex items-center gap-2">
              <Loader2 size={18} className="animate-spin" />
              Uploading...
            </div>
          </div>
        )}

        {filePreviews.length > 0 && (
          <div className="mt-4 space-y-2">
            {filePreviews.map((preview) => (
              <div
                key={preview.id}
                onClick={() => openFile(preview)}
                className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-white hover:border-blue-200 transition-all duration-200 ${
                  isUploading
                    ? "opacity-75 cursor-not-allowed"
                    : "cursor-pointer"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-gray-500">
                    {preview.type.startsWith("image/") ? (
                      <img
                        src={
                          preview.url.startsWith("blob")
                            ? preview.url
                            : `${backendUrl}${preview.url}`
                        }
                        alt={preview.name}
                        className="w-8 h-8 object-cover rounded"
                      />
                    ) : (
                      getFileTypeIcon(preview.type)
                    )}
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                      {preview.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {preview.isExisting
                        ? "Existing file"
                        : formatFileSize(preview.size)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(preview.id);
                    }}
                    className={`p-1.5 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50 transition-colors ${
                      isUploading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    title="Eliminar archivo"
                    disabled={isUploading}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {!onlyTextInput && (
        <div className="border-t border-gray-100 p-2 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt,.rtf,.zip,.rar,.7z,.mp4,.mov,.avi,.webm,.xls,.xlsx,.csv,.html,.jpg,.jpeg,.png"
              onChange={(e) => handleFileSelect(e.target.files)}
              disabled={isUploading}
            />
            <button
              onClick={() => !isUploading && fileInputRef.current?.click()}
              className={`p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-blue-500 transition-colors ${
                isUploading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              title="Adjuntar archivos"
              disabled={isUploading}
            >
              <Paperclip size={18} />
            </button>
            <div className="relative">
              <button
                onClick={() =>
                  !isUploading && setShowEmojiPicker(!showEmojiPicker)
                }
                className={`p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-blue-500 transition-colors ${
                  isUploading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                title="Agregar emoji"
                disabled={isUploading}
              >
                <Smile size={18} />
              </button>
              {showEmojiPicker && !isUploading && (
                <div className="absolute bottom-full left-0 mb-2">
                  <div className="relative">
                    <div
                      className="fixed inset-0"
                      onClick={() => setShowEmojiPicker(false)}
                    />
                    <EmojiPicker
                      onEmojiClick={handleEmojiSelect}
                      autoFocusSearch={false}
                      lazyLoadEmojis
                      searchPlaceHolder="Search emoji..."
                      width={320}
                      height={400}
                    />
                  </div>
                </div>
              )}
            </div>
            {recentEmojis.length > 0 && (
              <div className="flex items-center gap-1 px-2">
                {recentEmojis.map((emoji, index) => (
                  <button
                    key={index}
                    onClick={() => insertRecentEmoji(emoji)}
                    className={`hover:bg-gray-100 rounded p-1 text-lg transition-colors ${
                      isUploading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    disabled={isUploading}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={handlePublish}
            className={`w-9 h-9 flex items-center justify-center rounded-full bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-500 transition-colors`}
            title={buttonText}
            disabled={isUploading}
          >
            {isUploading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} className="-ml-0.5" />
            )}
          </button>
        </div>
      )}

      {selectedFile && (
        <FileViewerModal
          file={selectedFile}
          onClose={() => setSelectedFile(null)}
          backendUrl={backendUrl}
        />
      )}
    </div>
  );
}
